import xws from 'api/xws/';
import qs from 'query-string';
import { createAction } from 'redux-actions';
import type { Thunk } from 'types/redux';
import loginWS from 'api/xws/login/loginWs';
import { signUpWS, type SignUpResponse } from 'api/xws/signUp/signUpWs';
import lostUserNameWs from 'api/xws/login/lostUserNameWs';
import lostPasswordWs from 'api/xws/login/lostPasswordWs';
import merge from 'deepmerge';

// Action Types
import * as actionTypes from './actionTypes';

// Actions
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import { setAppFlow } from 'store/reducers/userInteractions/actions';
import { goTo } from 'store/reducers/navigation/actions';
import { setContactInfo } from 'store/reducers/appointment/actions';
import { triggerShiftEvent } from 'store/reducers/clickTracking/actions';

// Selectors
import {
  getWebkey,
  getUnObfuscatedObject
} from 'store/reducers/queryParams/selectors';
import { getTokenId } from 'store/reducers/tokenId/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import {
  getRecaptchaToken,
  isRecaptchaEnabledOnRegisterUser,
  getShiftDigitalEnabled
} from 'store/reducers/dealerSettings/selectors';
import { getConfirmationKey } from 'store/reducers/appointment/selectors';
import {
  getPhoneNumber,
  getFirstPhoneNumber
} from 'store/reducers/user/personInfo/selectors';
import { getShiftServiceLoginObj } from 'store/reducers/clickTracking/selectors';

// Constants
import { getService } from 'constants/ajax';
import { CHANNEL } from 'constants/appConfig';
import { APP_FLOW } from 'constants/appFlow';
import { USER_VEHICLES_ROUTE } from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';

// Entities
import type { PersonInfo } from 'api/xws/login/entities/PersonInfo';
import { actions as personInfoActions } from 'store/reducers/user/personInfo/';

export const LOGIN_SUCCESS = 'user/LOGIN_SUCCESS';
const loginSuccess = createAction(LOGIN_SUCCESS);
const { FORWARD } = NAVIGATION;
const { LOG_ME_IN } = APP_FLOW;

type LoginResponse = {|
  success: number,
  statusCode: number,
  key: string,
  authTokenId: string,
  personInfo: PersonInfo
|};

const getContactPhoneNumber = contactInfo => (dispatch, getState) =>
  getPhoneNumber(getState())('HOME') || getFirstPhoneNumber(contactInfo);

const getCompletePersonInfo = data => dispatch => {
  const phoneNumber = {
    contactInfo: {
      phoneNumbers: [
        {
          type: 'HOME',
          digits: dispatch(getContactPhoneNumber(data.contactInfo))
        },
        {
          type: 'MOBILE',
          digits: data.contactInfo.phoneNumbers[2].digits
        }
      ]
    }
  };
  const overwriteMerge = (destinationArray, sourceArray, options) =>
    sourceArray;
  return merge(data, phoneNumber, {
    arrayMerge: overwriteMerge
  });
};

const getContactInfo = data => (dispatch, getState) => ({
  firstName: data.personInfo.firstName,
  lastName: data.personInfo.lastName,
  phone: dispatch(getContactPhoneNumber(data.personInfo.contactInfo)),
  mobilePhone: getPhoneNumber(getState())('MOBILE'),
  email: data.personInfo.contactInfo.emailAddress.email
});

export const doLogin = (
  userName: string,
  password: string
): Thunk<Promise<LoginResponse>> => async (dispatch, getState) => {
  const webKey = getWebkey(getState());
  const tokenId = getTokenId(getState());

  try {
    const { data } = await loginWS({
      webKey,
      tokenId,
      locale: getLocaleCode(getState()),
      channel: CHANNEL,
      userName,
      password
    });
    dispatch(loginSuccess(data));
    if (data.success) {
      const completePersonInfo = dispatch(
        getCompletePersonInfo(data.personInfo)
      );
      const contactInfo = dispatch(getContactInfo(data));
      dispatch(personInfoActions.setPersonInfo(completePersonInfo));
      dispatch(setContactInfo(contactInfo));
      dispatch(setAppFlow(LOG_ME_IN));
      dispatch(sendScheduleServiceLogin());
      dispatch(goTo(USER_VEHICLES_ROUTE, FORWARD));
    }
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
  }
};

export const getAutoLogin = () => async (dispatch, getState) => {
  const autoLoginService = getService('autoLogin');
  const webKey = getWebkey(getState());
  const { username: userName, password } = getUnObfuscatedObject(getState());
  const tokenId = getTokenId(getState());
  autoLoginService.setCallBacks(dispatch(ajaxCallbacks()));
  const request = xws(autoLoginService).post(
    autoLoginService.getPath(),
    qs.stringify({
      tokenId,
      webKey,
      userName,
      password,
      channel: CHANNEL
    })
  );
  try {
    const { data } = await request;
    dispatch(setLoginData(data.personInfo));
    const completePersonInfo = dispatch(getCompletePersonInfo(data.personInfo));
    const contactInfo = dispatch(getContactInfo(data));
    dispatch(personInfoActions.setPersonInfo(completePersonInfo));
    dispatch(setContactInfo(contactInfo));
    return data.personInfo;
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
    /* eslint-enable no-console */
    return false;
  }
};

export const setLoginData = loginData => {
  return {
    type: actionTypes.SET_LOGIN_DATA,
    payload: loginData
  };
};

export const forgotUserName = (email: string): Thunk<Promise<any>> => async (
  dispatch,
  getState
) => {
  const webKey = getWebkey(getState());
  const tokenId = getTokenId(getState());
  try {
    const { data } = await lostUserNameWs({
      webKey,
      tokenId,
      email
    });
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
    return { success: false };
  }
};

export const forgotPassword = (
  username: string,
  email: string
): Thunk<Promise<any>> => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const tokenId = getTokenId(getState());
  try {
    const { data } = await lostPasswordWs({
      webkey,
      tokenId,
      email,
      username
    });
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
    return { success: false };
  }
};

export const signUp = (
  userName: string,
  password: string
): Thunk<Promise<SignUpResponse>> => async (dispatch, getState) => {
  const webKey = getWebkey(getState());
  const tokenId = getTokenId(getState());
  const confirmationKey = getConfirmationKey(getState());
  const gRecaptchaResponse = getRecaptchaToken(getState());
  const sendRecaptchaToken = isRecaptchaEnabledOnRegisterUser(getState());

  try {
    const { data } = await signUpWS({
      webKey,
      tokenId,
      userName,
      password,
      confirmationKey,
      ...(sendRecaptchaToken && { gRecaptchaResponse })
    });
    if (data.success) {
      await dispatch(doLogin(userName, password));
    }
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
    return { success: false };
  }
};

export const sendScheduleServiceLogin = params => async (
  dispatch,
  getState
) => {
  const state = getState();

  if (getShiftDigitalEnabled(state)) {
    const shiftobj = getShiftServiceLoginObj(state);
    dispatch(triggerShiftEvent(shiftobj));
  }
};



// WEBPACK FOOTER //
// ./src/store/reducers/login/actions.js